import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { identifyUser, resetAnalytics } from "@/lib/analytics";

export interface Profile {
  id: string;
  email: string | null;
  display_name: string | null;
  preferred_lang: string | null;
  exam_date: string | null;
  applicant_track: string | null;
  preferred_courses: string[] | null;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profile: Profile | null;
  isAdmin: boolean;
  hasActiveAccess: boolean;
  subscriptionTier: string | null;
  accessExpiry: string | null;
  signOut: () => Promise<void>;
  refreshEntitlements: () => Promise<void>;
  updateProfile: (patch: Partial<Profile>) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Plan strength — a repeat purchase must never downgrade a member. */
const TIER_RANK: Record<string, number> = { pro_6: 4, pro_3: 3, core_3: 2, free: 1 };

type EntitlementRow = { tier: string | null; access_expiry: string | null };

export function pickBestEntitlement(rows: EntitlementRow[]): EntitlementRow | null {
  if (!rows.length) return null;
  return [...rows].sort((a, b) => {
    const rank = (TIER_RANK[b.tier ?? "free"] ?? 0) - (TIER_RANK[a.tier ?? "free"] ?? 0);
    if (rank !== 0) return rank;
    return new Date(b.access_expiry ?? 0).getTime() - new Date(a.access_expiry ?? 0).getTime();
  })[0];
}


export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [accessExpiry, setAccessExpiry] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUserData = async (uid: string) => {
    // Defer the supabase calls so we don't block the auth callback.
    const [{ data: profileData }, { data: rolesData }, { data: subRows }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", uid),
      // Fetch ALL unexpired active purchases. The effective entitlement is the
      // STRONGEST tier held (pro_6 > pro_3 > core_3), not the row that happens
      // to expire last — otherwise a cheaper repeat purchase would silently
      // downgrade a member. This mirrors the server-side current_tier().
      supabase
        .from("subscriptions")
        .select("tier, access_expiry")
        .eq("user_id", uid)
        .eq("status", "active")
        .is("refunded_at", null)
        .gt("access_expiry", new Date().toISOString()),
    ]);


    setProfile(
      profileData
        ? {
            id: profileData.id,
            email: profileData.email,
            display_name: profileData.display_name,
            preferred_lang: profileData.preferred_lang,
            exam_date: profileData.exam_date,
            applicant_track: (profileData as { applicant_track?: string | null }).applicant_track ?? null,
            preferred_courses: (profileData as { preferred_courses?: string[] | null }).preferred_courses ?? null,
          }
        : null,
    );
    setIsAdmin(!!rolesData?.some((r) => r.role === "admin"));
    const best = pickBestEntitlement(subRows ?? []);
    setSubscriptionTier(best?.tier ?? null);
    setAccessExpiry(best?.access_expiry ?? null);
  };


  useEffect(() => {
    // 1. Synchronous listener first.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        // Defer fetches to avoid deadlocks inside the callback.
        setTimeout(() => {
          loadUserData(newSession.user.id);
          void identifyUser(newSession.user.id, newSession.user.email);
        }, 0);
      } else {
        setProfile(null);
        setIsAdmin(false);
        setSubscriptionTier(null);
        setAccessExpiry(null);
        resetAnalytics();
      }
    });

    // 2. Then check existing session.
    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) {
        loadUserData(existing.user.id).finally(() => setLoading(false));
        void identifyUser(existing.user.id, existing.user.email);
      } else {
        setLoading(false);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  // hasActiveAccess is derived from subscriptionTier, which is computed
  // from the subscriptions table (status='active' AND access_expiry >
  // now()). Single source of truth — same source current_tier() uses
  // server-side. Do NOT reintroduce a profiles.access_expiry read here;
  // that denormalized column drifts from subscriptions and is being retired.
  const hasActiveAccess = subscriptionTier !== null;

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshEntitlements = async () => {
    if (user?.id) await loadUserData(user.id);
  };

  const updateProfile = (patch: Partial<Profile>) => {
    setProfile((current) => (current ? { ...current, ...patch } : current));
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, profile, isAdmin, hasActiveAccess, subscriptionTier, accessExpiry, signOut, refreshEntitlements, updateProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};